import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import "./CreatePasswordPage.css";
import playfulDog from "../../../assets/dogLevel3.png";
import apiClient from "../../../api/apiClient";
import { useGlobalAlert } from "../../../context/AlertContext";
import socialPawsLogo from "../../../assets/appIcon.svg";
import MobileTopBar from "../../../components/MobileTopBar";
import DesktopTopBar from "../../../components/DesktopTopBar";

const CreatePasswordPage = () => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();
  const location = useLocation();

  const email = location.state?.email || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [requirements, setRequirements] = useState({
    match: false,
    length: false,
    complexity: false,
  });

  const isFormValid = Object.values(requirements).every(Boolean) && email;

  useEffect(() => {
    const lengthValid = password.length >= 8 && password.length <= 20;
    const complexityValid = /[\d]|[^A-Za-z0-9]/.test(password);
    const matchValid = password.length > 0 && password === confirmPassword;

    setRequirements({
      length: lengthValid,
      complexity: complexityValid,
      match: matchValid,
    });
  }, [password, confirmPassword]);

  const handleUpdate = async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const response = await apiClient.post("/user/change-password", {
        user_email: email,
        new_password: password,
      });

      if (response.data.status) {
        console.log("Password Updated Successfully");
        navigate("/password-updated");
      } else {
        triggerAlert(
          response.data.message ||
            "Failed to update password. Please try again.",
          "error",
        );
      }
    } catch (error) {
      console.error("Password Update Failed", error);
      const errorMessage =
        error.response?.data?.message ||
        "Something went wrong. Please check your connection.";
      triggerAlert(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cp-page-container">
      {/* Desktop Header (Hidden on Mobile) */}
      {/* <div className="fp-header fp-desktop-only">
        <button className="fp-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} color="#fff" strokeWidth={3} />
          <span>Back</span>
        </button>
      </div> */}
      <DesktopTopBar
        showBack={true}
        onBack={() => navigate(-1)}
        backColor="#fff"
        showProgress={false}
      />

      {/* Mobile Header (Hidden on Desktop) */}
      {/* <div className="fp-mobile-header fp-mobile-only">
        <button
          className="fp-back-btn fp-mobile-back-override"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={22} color="#fff" strokeWidth={3} />
          <span>Back</span>
        </button>
        <div className="fp-header-logo">
          <img src={socialPawsLogo} alt="Social Paws" className="fp-logo-img" />
          <span>Social Paws</span>
        </div>
      </div> */}
      <MobileTopBar
        onBack={() => navigate(-1)}
        backColor="#fff"
        logoColor="#fff"
        isProtectedRoute={false}
      />

      {/* Main Content */}
      <div className="cp-content-wrapper">
        <div className="cp-mascot-container">
          <img src={playfulDog} alt="Playful Dog" />
        </div>

        <h1 className="cp-title">Create New Password?</h1>

        <p className="cp-subtitle">
          Enter a secure password with at least 8 characters,
          <br />
          including one symbol and one number.
        </p>

        <div className="cp-form-area">
          {/* Password Field */}
          <div className="cp-input-group">
            <label>PASSWORD</label>
            <div className="cp-input-wrapper">
              <input
                type={showPass ? "text" : "password"}
                placeholder="Must be min. 8 characters"
                className="cp-input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="cp-eye-btn"
                onClick={() => setShowPass(!showPass)}
              >
                {showPass ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div className="cp-input-group">
            <label>CONFIRM PASSWORD</label>
            <div className="cp-input-wrapper">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Passwords must match"
                className="cp-input-field"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="cp-eye-btn"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>

          {/* Validation Checklist */}
          <div className="cp-validation-list">
            <div className={`cp-valid-item ${requirements.match ? "met" : ""}`}>
              <AlertCircle
                size={16}
                fill={requirements.match ? "#47D7AC" : "transparent"}
                stroke={"#009FDC"}
              />
              <span>Passwords must match</span>
            </div>
            <div
              className={`cp-valid-item ${requirements.length ? "met" : ""}`}
            >
              <AlertCircle
                size={16}
                fill={requirements.length ? "#47D7AC" : "transparent"}
                stroke={"#009FDC"}
              />
              <span>Must be 8-20 characters</span>
            </div>
            <div
              className={`cp-valid-item ${requirements.complexity ? "met" : ""}`}
            >
              <AlertCircle
                size={16}
                fill={requirements.complexity ? "#47D7AC" : "transparent"}
                stroke={"#009FDC"}
              />
              <span>
                Must include at least one number or one special character (e.g
                $%&)
              </span>
            </div>
          </div>

          {/* Submit Button - Disabled if not valid */}
          <button
            className={`cp-update-btn ${!isFormValid || isSubmitting ? "disabled" : ""}`}
            onClick={handleUpdate}
            disabled={!isFormValid || isSubmitting}
            style={{
              opacity: isFormValid && !isSubmitting ? 1 : 0.5,
              cursor: isFormValid && !isSubmitting ? "pointer" : "not-allowed",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                UPDATING...
              </>
            ) : (
              "UPDATE PASSWORD"
            )}
          </button>

          <div className="cp-signup-link">
            Don't have an account?{" "}
            <span onClick={() => navigate("/onboarding")}>Sign Up Here</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePasswordPage;
